import { Bike, Boxes, Motorbike, Scooter, Trees, X, Zap, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

const productIcons: Record<string, LucideIcon> = {
    moto: Motorbike,
    scooter: Scooter,
    '50': Scooter,
    quad: Trees,
    verte: Trees,
    trottinette: Zap,
    velo: Bike,
    livraison: Boxes,
};

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ProductPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (key: string) => void;
    /** Types de véhicule proposés : clé (`moto`, `scooter`, `velo`…) → libellé. */
    options: Record<string, ReactNode>;
    title?: string;
}

const ProductPickerModal = ({ isOpen, onClose, onSelect, options, title = 'Choisissez le type de véhicule' }: ProductPickerModalProps) => {
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);
    const lastFocusedRef = useRef<Element | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        lastFocusedRef.current = document.activeElement;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        const focusTimer = setTimeout(() => {
            if (dialogRef.current) {
                const firstFocusable = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
                if (firstFocusable) firstFocusable.focus();
            }
        }, 50);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            clearTimeout(focusTimer);
            const lastFocused = lastFocusedRef.current as HTMLElement | null;
            if (lastFocused && typeof lastFocused.focus === 'function') {
                lastFocused.focus();
            }
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="bg-card rounded-xl shadow-lg w-full max-w-lg max-h-[80vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-6 pb-6 border-b">
                    <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Fermer la fenêtre"
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>

                <div className="p-6 space-y-3">
                    {Object.entries(options).map(([key, label]) => {
                        const Icon = productIcons[key] || Motorbike;

                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => {
                                    onSelect(key);
                                    onClose();
                                }}
                                className="w-full flex items-center gap-4 p-6 rounded-lg border border-border bg-card text-left cursor-pointer transition-all duration-200 hover:border-primary/30 hover:bg-primary/5 shadow-sm"
                            >
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted/50 text-muted-foreground">
                                    <Icon className="w-5 h-5" aria-hidden="true" />
                                </div>
                                <p className="font-medium text-sm">{label}</p>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ProductPickerModal;
